import { useEffect, useState, useMemo, Fragment, useRef } from "react";
import { withRouter } from "react-router-dom";
import { connect, useDispatch } from "react-redux";
import _ from "lodash";
import { utils, writeFileXLSX } from 'xlsx';

import { Box, Divider, FormGroup, Grid, InputAdornment, Menu, MenuItem, Paper, Radio, RadioGroup, Select, Tooltip, Typography } from "@mui/material";
import moment, {Moment} from "moment-timezone";
import QuestionMarkOutlinedIcon from '@mui/icons-material/Help';

import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { Button, TextField, Dialog } from '@mui/material';
import { Search, Download, DataUsage } from '@mui/icons-material';
import TuneIcon from '@mui/icons-material/Tune';
import AssetUsageBarChart from "./AssetUsageBarChart";
import AssetsTable from "./AssetsTable";
import { Pagination } from "../Pagination";

import { parentDpAndSubDpList, updatePermission } from '../../actions/Users/authenticate';
import { checkLoggedInUserAuthorizedToViewPage, convertDateTimeIntoTimezone, getDataFromLocalStorageParsed, getDecodeURI, getEncodedURI } from '../../utils/util';
import { Asset, AssetSortBy, ServiceLine, StarlinkAvailabilityAlert, StarlinkQuotaAlert, StarlinkReportsProps } from "./types";
import { clearCSV, clearComponentData, getAssetUsage, getAssetUsageCharts, getAssets, getAssetsCSV, setQuotaAlert, setAvailabilityAlert, getAllServiceLines } from "./slice";

import "./index.css";
import { SortOrder } from "../SortArrows";
import QuotaAlertDialog from "../StarlinkQuota/AddEditQuotaDialog";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from '@mui/icons-material/Close';
import NotificationAddIcon from '@mui/icons-material/NotificationAdd';
import DownloadIcon from '../../asset/image/DownloadUsage.svg';
import { CsvParameters, downloadAsCSV } from "../../actions/Users/authenticateCsvDownload";
import { GET_CSV_DATA_TO_DOWNLOAD } from "../../actions/types";
import DownloadCSV, { clearCsvDownload } from "../DownloadCSV";
import AvailabiltyAlertDialog from "./AvailabiltyAlertDialog";
import { STARLINK_DATAUSAGE_CATEGORIES, STARLINK_DATAUSAGE_CATEGORIES_DATA } from "../../UserScreen/Starlink/types";
import { DEFAULT_DATE_FORMAT, YYYYMMDD_HHmmss } from "../../utils/constants";
import { setSelectedAccount } from "../../UserScreen/Starlink/slice";
import { getLastThreeMonths } from "../../constants/Constants";
import { SERVICE_FUNCTION_TO_FEATURE } from "../../config";
import close from "../../asset/image/close.svg";
import { isAuthorizedFor } from "../../utils/util";

const convertToCsvUsage = (data: any[][]) => {
    const [_columns, ...rows] = data;
    const csvRows: any[] = [];
    rows.forEach(row => {
        csvRows.push([row[0], row[1], row[2], row[3], row[4], row[5], row[6], row[7], row[8], row[9], 
            row[10], row[11], row[12], row[13], row[14], row[15], row[16], row[17], row[18], row[19],
            row[20], row[21], row[22], row[23], row[24], row[25], row[26], row[27], row[28], row[29],
            row[30], row[31], getKitSerialNumbers(row[32]), row[33], row[34], row[35], row[36], row[37], row[38]
        ])
    })
    return [['Dp Id', 'Account Number', 'Asset Number', 'Account Name', 'Active', 'Account Dp Id', 'Account Dp Name', 'Dp Name', 'Asset Display Name', 'Billing Day of Month', 
        'Usage Limit (GB)', 'Current Billing USage (GB)', 'Billing Usage Percentage', 'Subscription Start date', 'Subscription Line Usage Limit (GB)', 'Current Billing Start Date', 'Overage (GB)', 'Overage', 'Subscription Line Description', 'Is Custom Plan',
        'Opted In', 'Is Service Line Paused', 'Total Topup Limit (GB)', 'Total Topup Consumed (GB)', 'MTD Priority (GB)', 'MTD OptIn Priority (GB)', 'MTD Standard (GB)', 'MTD Non-billing (GB)', 'MTD Usage', 'Is Pooled Account',
        'K4 Site Id', 'K4 Site Name', 'User Terminals', 'Chart Usage', 'Mobile Priority Data', 'Local Priority', 'Global Priority', 'Legacy Plans Data', 'Other Data',
    ], ...csvRows]
}

const convertToCsvUserUsage = (data: any[][]) => {
    const [_columns, ...rows] = data;
    const csvRows: any[] = [];
    rows.forEach(row => {
        csvRows.push([row[8], row[2], row[7], row[18], row[35], row[34], getKitSerialNumbers(row[32])
        ])
    })
    return [[ 'Service Line', 'Service Line Number', 'Sub Organization', 'Subscription', 'Local Priority', 'Mobile Priority', 'User Terminal',
    ], ...csvRows]
}

const getKitSerialNumbers = (userTerminals: string): string => {
  return JSON.parse(userTerminals)
    .map((item: any) => item.kitSerialNumber)
    .join(", ");
};

function StarlinkReports(props: StarlinkReportsProps) {
    const { history, location, dpIds, assets, assetsCSV, assetUsage, getAssetUsageCharts, selectedServiceAccount, totalAssets, getSubscribedServiceLines, newSummaryServiceAccount, hasUserChangedServiceLine, getAssets, setQuotaAlert, setAvailabilityAlert, clearComponentData, newSummaryStartDate, newSummaryEndDate, newSummaryServiceLine, authReducer, downloadAsCSV, getAllServiceLines } = props;

    const queryParams = new Map<string, string>(Object.entries(getDecodeURI(location.search)));
    const isAllServiceLineUser = getDataFromLocalStorageParsed("loggedInUser")?.data?.user?.allServiceLineUser
    const [searchText, setSearchText] = useState<string>(queryParams.get('search') || '');
    const [openBillingDialog, setOpenBillingDialog] = useState(false);
    const STARLINK_ADMIN_SF = "starlink-admin";
    const starlinkAdminService = SERVICE_FUNCTION_TO_FEATURE[STARLINK_ADMIN_SF]["service"];
    const starlinkAdminFunction = SERVICE_FUNCTION_TO_FEATURE[STARLINK_ADMIN_SF]["function"];
    const lastThreeMonths = getLastThreeMonths()
    const currentMonth = {
        label: moment().format('MMMM'),
        value: moment().format('YYYY-MM'),
        startDate: moment().utc().startOf('month').format('YYYY-MM-DD 00:00:00'),
        endDate: moment().utc().endOf('month').format('YYYY-MM-DD 23:59:59')
    };
    const defaultFilters = [
        { name: "Global Priority", label: "global_priority", selected: false },
        { name: "Local Priority", label: "local_priority", selected: false },
    ];

    const [selectedFilters, setSelectedFilters] = useState("standard");

    const handleFilterChange = (newFilter: string) => {
        setSelectedFilters(newFilter);
    };
    const isAdmin = isAuthorizedFor("admin");
    const isFull = isAuthorizedFor("full");
    const isUser = isAuthorizedFor("user");

    const prevServiceLinesRef = useRef();
    const prevValuesRef = useRef({
        newSummaryServiceLine: null,
        newSummaryEndDate: moment(new Date()),
        newSummaryStartDate: moment(new Date()),
    });

    const [selectedDownloadPeriod, setSelectedDownloadPeriod] = useState<any>(currentMonth)
    const lastThreeMonthsWithCurrent = [currentMonth, ...lastThreeMonths];

    const [tableParams, setTableParams] = useState<{
        page: number;
        size: number;
        sortBy: AssetSortBy;
        sortOrder: SortOrder;
        search: string;
        startDate: moment.Moment;
        endDate: moment.Moment;
        serviceLines: any;
    }>({
        page: queryParams.get('page') ? Number(queryParams.get('page')) : 1,
        size: queryParams.get('size') ? Number(queryParams.get('size')) : 10,
        sortBy: queryParams.get('sortBy') as AssetSortBy || 'chart_usage',
        sortOrder: queryParams.get('sortOrder') as SortOrder || 'desc',
        search: queryParams.get('search') || '',
        startDate: newSummaryStartDate,
        endDate: newSummaryEndDate,
        serviceLines: newSummaryServiceLine
    });
    const [selectedAssets, setSelectedAssets] = useState<Asset[]>([])
    const [showCurrentBillingCycleUsage, setShowCurrentBillingCycleUsage] = useState(false);
    const [disableExport, setDisableExport] = useState(false);
    const [includeInactiveServiceLines, setIncludeInactiveServiceLines] = useState(false);

    const [openQuotaAlertDialog, setOpenQuotaAlertDialog] = useState(false);
    const [serviceLineQuotaToEdit, setServiceLineQuotaToEdit] = useState<ServiceLine | null>(null);
    const [openAvailabilityAlertDialog, setOpenAvailabilityAlertDialog] = useState(false);
    const [serviceLineAvailabilityToEdit, setServiceLineAvailabilityToEdit] = useState<ServiceLine | null>(null);


    const searchTimeoutRef = useRef<any>();
    const dispatch = useDispatch();



    const getSource = () => {
        const endDate = moment(tableParams.endDate, 'YYYY-MM-DD HH:mm:ss');
        const startDate = moment(tableParams.startDate, 'YYYY-MM-DD HH:mm:ss');

        const hoursDifference = Math.floor(moment.duration(endDate.diff(startDate)).asHours());
        if (hoursDifference <= 24) {
            return 'aggregation_table';
        } else if (hoursDifference > 24) {
            return 'direct_source';
        } else {
            return ''
        }
    }

    const updateQueryParams = () => {
        queryParams.set('page', tableParams.page.toString())
        queryParams.set('size', tableParams.size.toString())
        queryParams.set('sortBy', tableParams.sortBy)
        queryParams.set('sortOrder', tableParams.sortOrder)
        queryParams.set('search', tableParams.search)
        // queryParams.set('startDate', tableParams.startDate.valueOf().toString())
        // queryParams.set('endDate', tableParams.endDate.valueOf().toString())
        history.replace({ pathname: location.pathname, search: `?${getEncodedURI(Object.fromEntries(queryParams.entries()))}` });
    }

    const transformAssetsCSV = (assetsCSV: Asset[]) => {
        // change column names
        const data = assetsCSV.map(asset => {
            const sulStart = moment(asset.subscription_line_start_date)
            const currBillingStart = moment(asset.current_billing_start_date)
            return {
                "DP ID": asset.dp_id,
                "DP Name": asset.dp_name,
                "Account Number": asset.account_number,
                "Account Name": asset.account_name,
                "Asset Number": asset.asset_number,
                "Asset Name": asset.asset_display_name,
                "Starlink ID": asset.user_terminal_id,
                "KIT Number": asset.user_terminal_serial_number,
                "Service Plan": asset.subscription_line_description,
                "Quantity": asset.subscription_line_quantity,
                "Service Plan Start Date": sulStart.isValid() ? sulStart.format('YYYY-MM-DD') : '',
                "Active": asset.active ? 'Yes' : 'No',
                "Billing Date": currBillingStart.isValid() ? currBillingStart.format('YYYY-MM-DD') : '',
                "Billing Usage (GB)": asset.curr_billing_cycle_usage,
            }
        });
        return data;
    }

    useEffect(() => {
        if (assetsCSV?.length > 0) {
            const data = transformAssetsCSV(assetsCSV);
            const ws = utils.json_to_sheet(data, {
                dateNF: 'YYYY-MM-DD'
            });
            const wb = utils.book_new();
            utils.book_append_sheet(wb, ws, "Service Lines");

            const ws2 = utils.json_to_sheet(Object.keys(assetUsage).map(assetNumber => {
                return assetUsage[assetNumber].map(assetUsage => {
                    return {
                        "Asset Number": assetNumber,
                        "Asset Name": assetUsage.asset_name,
                        "Date": moment(assetUsage.x).format('DD MMM YYYY'),
                        "Usage (GB)": assetUsage.y
                    }
                })
            }).flat(), {
                dateNF: 'YYYY-MM-DD'
            });
            utils.book_append_sheet(wb, ws2, "Usage");
            writeFileXLSX(wb, "starlink-report.xlsx");
        }
    }, [assetsCSV]);

    const handleChangePage = (event: any, newPage: number) => {
        setTableParams({ ...tableParams, page: newPage });
    };

    const handleChangeSize = (event: any) => {
        setTableParams({ ...tableParams, size: event.target.value, page: 1 });
    };

    const handleChangeSelectedAssets = (event: any) => {
        const { name, checked } = event.target;
        if (name === 'selectAll') {
            if (checked) {
                setSelectedAssets(assets);
            } else {
                setSelectedAssets([]);
            }
        } else {
            if (checked) {
                const asset = assets.find(asset => asset.asset_number === name);
                if (asset) {
                    setSelectedAssets([...selectedAssets, asset]);
                }
            } else {
                setSelectedAssets(selectedAssets.filter(asset => asset.asset_number !== name));
            }
        }
    }


    const handleOnSearchClick = () => {
        handleSearch(searchText);
    }

    const handleOnSearchClear = () => {
        setSearchText('');
        handleSearch('');
    }

    const handleOnSearchChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        setSearchText(e.target.value);
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
        searchTimeoutRef.current = setTimeout(() => {
            searchTimeoutRef.current = undefined;
            handleSearch(e.target.value);
        }, 1000)
    }


    const handleSearch = (value: string) => {
        setTableParams({
            ...tableParams,
            search: value,
            page: 1
        });
    }

    const handleChangeSorting = (sortBy: AssetSortBy, sortOrder: SortOrder) => {
        setTableParams({
            ...tableParams,
            sortBy: sortBy,
            sortOrder: sortOrder,
            page: 1
        });
    }

    const handleExportCSV = async () => {
        setDisableExport(true);
        handleDownloadReport();
        setDisableExport(false);
    }


    const handleCloseQuotaAlertDialog = () => {
        setOpenQuotaAlertDialog(false);
        setServiceLineQuotaToEdit(null);
    }

    const handleEditServiceLineQuota = (serviceLine: ServiceLine) => {
        setServiceLineQuotaToEdit(serviceLine);
        setOpenQuotaAlertDialog(true);
    }


    const handleSetQuotaAlert = async (quotaAlerts: StarlinkQuotaAlert[]) => {
        if (quotaAlerts.length > 0) {
            await setQuotaAlert({
                serviceLineNumbers: quotaAlerts.map(quotaAlert => quotaAlert.serviceLineNumber),
                alertingThresholds: quotaAlerts[0].alertingThresholds,
                notificationMethods: quotaAlerts[0].notificationMethods,
                usersToNotify: quotaAlerts[0].usersToNotify
            })
        }
        setOpenQuotaAlertDialog(false);
        setServiceLineQuotaToEdit(null);
        setTableParams({
            ...tableParams,
        });
    }

    const handleOpenAvailabilityAlertDialog = () => {
        setOpenAvailabilityAlertDialog(true);
    }

    const handleCloseAvailabilityAlertDialog = () => {
        setOpenAvailabilityAlertDialog(false);
        setServiceLineAvailabilityToEdit(null);
    }

    const handleEditServiceLineAvailability = (serviceLine: ServiceLine) => {
        setServiceLineAvailabilityToEdit(serviceLine);
        setOpenAvailabilityAlertDialog(true);
    }

    const handleSetAvailabilityAlert = async (availabilityAlerts: StarlinkAvailabilityAlert[]) => {
        if (availabilityAlerts.length > 0) {
            await setAvailabilityAlert({
                serviceLineNumbers: availabilityAlerts.map(quotaAlert => quotaAlert.serviceLineNumber),
                alertingThreshold: availabilityAlerts[0].alertingThreshold,
                notificationMethods: availabilityAlerts[0].notificationMethods,
                usersToNotify: availabilityAlerts[0].usersToNotify
            })
        }
        setOpenAvailabilityAlertDialog(false);
        setServiceLineAvailabilityToEdit(null);
        setTableParams({
            ...tableParams,
        });
    }

    const mapToCsvRow = (row: any) => {
        const values: any[] = Object.values(row);
        return [
            values[0], values[1], values[2], values[4], values[5], values[6], values[7], values[8], values[9], values[10], values[11], values[12], values[13], values[14], values[15],
        ]
    }


    useEffect(() => {
        if (authReducer.csvDataDownloded && authReducer.csvDataDownloded.length > 0) {

            if (authReducer.csvDownloadId === 'starlink-billing-usage') {
                DownloadCSV(convertToCsv(authReducer.csvDataDownloded), {
                    formatters: {
                        0: 'ESC-COMMA',
                        1: 'ESC-COMMA',
                        3: 'ESC-COMMA',
                        4: 'ESC-COMMA',
                        5: 'ESC-COMMA',
                        6: 'ESC-COMMA',
                        7: 'ESC-COMMA',
                    }
                });
                return () => {
                    dispatch(clearCsvDownload())
                }
            }
        }
    }, [authReducer.csvDataDownloded])

    const convertToCsv = (data) => {
        const csvData = data && data.length > 0 && data[1] && data[1]?.length > 0 && data[1][0]?.map(item => {
            const kitSerialNumbers = item?.kitSerialNumbers;
            return [item?.accountDpName, item?.dpName, item?.billingDate, item?.accountNumber, kitSerialNumbers, item?.serviceLineName, item?.serviceLineNotes ?? "", item?.serviceLineStatus ? 'Active' : 'Inactive', item?.planName, item?.totalLimit, item?.totalTopupLimit ? item?.totalTopupLimit : 0, item?.totalOptInPriorityGb, item?.totalStandardGb, item?.totalUsage,]
        })

        const headers: string[] = ['Organization', 'Sub-Organization', 'Billing Date', 'Account Number', 'Kit Serial Number', 'Service Line Name', 'Deployment', 'Service line status', 'Plan', 'Total Limit (GB)', 'Topup Limit (GB)', 'Total Opt In Priority (GB)', 'Total Standard (GB)', 'Total Usage (GB)'];
        return [headers, ...csvData];
    }

    const handleDownloadReport = () => {
        const params: CsvParameters = {
            type: 'QUERY_CH',
            id: 'starlink-usage',
            payload: {
                dpIds: dpIds?.map(dpId => `'${dpId}'`).join(','),
                accountNumbers: effectiveServiceLines === "''" && newSummaryServiceAccount?.length === selectedServiceAccount?.length ? "''" : selectedServiceAccount?.map(acc => `'${acc}'`).join(','),
                serviceLines: effectiveServiceLines === "''" || effectiveServiceLines === null ? "''" : effectiveServiceLines?.map(sl => `'${sl}'`).join(','),
                searchText: tableParams.search,
                chartStartDate: tableParams.startDate.format(YYYYMMDD_HHmmss),
                chartEndDate: tableParams.endDate.format(YYYYMMDD_HHmmss),
                sortBy: tableParams.sortBy,
                sortOrder: tableParams.sortOrder,
                limit: totalAssets || 100,
                offset: 0,
                usageSource: getSource(),
                // usage_filter: selectedFilters
                non_billable: selectedFilters && selectedFilters === "standard" || selectedFilters === "total" ? 1 : 0,
                standard: selectedFilters && selectedFilters === "standard" || selectedFilters === "total" ? 1 : 0,
                priority: selectedFilters && selectedFilters === "total" ? 1 : 0,
                opt_in_priority: selectedFilters && selectedFilters === "standard" || selectedFilters === "total" ? 1 : 0,
            },
            queryName: 'OPT_GET_STARLINK_ASSETS_CH_V3',
        }
        downloadAsCSV(params);
    }

    useEffect(() => {
        if (!authReducer.csvDataDownloded || !authReducer.csvDataDownloded.length) return;
        if (authReducer.csvDownloadId !== 'starlink-usage') return;
        DownloadCSV((!isAdmin && isUser) ? convertToCsvUserUsage(authReducer.csvDataDownloded) : convertToCsvUsage(authReducer.csvDataDownloded), {
            formatters: {
                1: 'ESC-COMMA',
                2: 'ESC-COMMA',
                3: 'ESC-COMMA',
                4: 'ESC-COMMA',
                5: 'ESC-COMMA',
                6: 'ESC-COMMA',
                7: 'ESC-COMMA',
                8: 'ESC-COMMA',
                32: 'ESC-COMMA'
            }
        });
        return () => {
            dispatch(clearCsvDownload())
        }
    }, [authReducer.csvDataDownloded])

    const handleDownloadBillingReport = () => {
        const params: CsvParameters = {
            type: 'QUERY_PG',
            id: 'starlink-billing-usage',
            payload: {
                "searchText": "",
                "sortBy": "serviceLineName",
                "sortOrder": "asc",
                "limit": 10000,
                "offset": 0,
                chartStartDate: selectedDownloadPeriod?.startDate,
                chartEndDate: selectedDownloadPeriod?.endDate,
                dpId: authReducer?.selectedOu?.id,
                // serviceLines: Array.isArray(newSummaryServiceLine) && newSummaryServiceLine?.filter(sl => sl !== '').map(sl => `'${sl}'`).join(',')
            },
            queryName: 'GET_SERVICE_LINES_BILLINGS_V3',
        }
        downloadAsCSV(params);
        setOpenBillingDialog(false)
        setSelectedDownloadPeriod(currentMonth)
    }

    useEffect(() => {
        return () => {
            clearComponentData();
        }
    }, [])

    useEffect(() => {
        if (!_.isEmpty(dpIds) && !_.isEqual(prevServiceLinesRef.current, newSummaryServiceLine)) {
            getAllServiceLines({
                dpIds: dpIds,
                serviceLines: Array.isArray(newSummaryServiceLine)
                    ? newSummaryServiceLine.filter(sl => sl !== '')
                    : [],
            });

            prevServiceLinesRef.current = newSummaryServiceLine;
        }
    }, [newSummaryServiceLine]);

    useEffect(() => {
        const hasChanged =
            !_.isEqual(prevValuesRef.current.newSummaryServiceLine, newSummaryServiceLine) ||
            !_.isEqual(prevValuesRef.current.newSummaryEndDate, newSummaryEndDate) ||
            !_.isEqual(prevValuesRef.current.newSummaryStartDate, newSummaryStartDate);

        if (hasChanged) {
            setTableParams({
                ...tableParams,
                startDate: newSummaryStartDate,
                endDate: newSummaryEndDate,
                serviceLines: Array.isArray(newSummaryServiceLine)
                    ? newSummaryServiceLine.filter(sl => sl !== '')
                    : [],
            });

            prevValuesRef.current = {
                newSummaryServiceLine,
                newSummaryEndDate,
                newSummaryStartDate,
            };
        }
    }, [newSummaryServiceLine, newSummaryEndDate, newSummaryStartDate, tableParams]);

    useEffect(() => {
        if (!_.isEmpty(dpIds)) {
            if (showCurrentBillingCycleUsage && selectedAssets?.length === 1) {
                const billingDayOfMonth = selectedAssets[0]?.billing_day_of_month;
                if (billingDayOfMonth) {
                    const currentBillingCycleStartDate = moment().date() < billingDayOfMonth ? moment().subtract(1, 'month').startOf('month').date(billingDayOfMonth) : moment().startOf('month').date(billingDayOfMonth);

                    const currentBillingCycleEndDate = moment().date() < billingDayOfMonth ? moment().startOf('month').date(billingDayOfMonth) : moment().add(1, 'month').startOf('month').date(billingDayOfMonth);

                    setTableParams({
                        ...tableParams,
                        startDate: currentBillingCycleStartDate,
                        endDate: currentBillingCycleEndDate
                    });
                }
            }
        }
    }, [showCurrentBillingCycleUsage])

    useEffect(() => {
        setShowCurrentBillingCycleUsage(false);
    }, [selectedAssets])

    useEffect(() => {
        if (queryParams.get('interval') === '1h' || queryParams.get('interval') === '6h' || queryParams.get('interval') === '12h') {
            queryParams.set('interval', '1d')
            queryParams.set('startDate', moment(Date.now()).subtract(1, 'day').valueOf().toString())
            queryParams.set('endDate', moment(Date.now()).valueOf().toString())
        }
    }, [])

    type TableParams = {
        page: number;
        size: number;
        sortBy: string;
        sortOrder: string;
        search: string;
        startDate: Moment;
        endDate: Moment;
        serviceLines: any;
    };

    const prevTableParamsRef = useRef<TableParams | null>(null);
    const prevIncludeInactiveServiceLinesRef = useRef<boolean>(false);

    const [effectiveServiceLines, setEffectiveServiceLines] = useState<any[] | "''" | null>(null);

    useEffect(() => {
        if (isAllServiceLineUser && !hasUserChangedServiceLine) {
            setEffectiveServiceLines("''");
            return;
        }

        if (isAllServiceLineUser && hasUserChangedServiceLine) {
            if (Array.isArray(newSummaryServiceLine) && newSummaryServiceLine.length > 0) {
                setEffectiveServiceLines(newSummaryServiceLine);
            }
            return;
        }

        if (Array.isArray(newSummaryServiceLine)) {
            setEffectiveServiceLines(newSummaryServiceLine);
        }

    }, [isAllServiceLineUser, JSON.stringify(newSummaryServiceLine)]);

    useEffect(() => {
        const hasChanged = !_.isEqual(prevTableParamsRef.current, tableParams) || prevIncludeInactiveServiceLinesRef.current !== includeInactiveServiceLines;
        if (_.isEmpty(tableParams)) return;
        if (effectiveServiceLines === null || effectiveServiceLines?.length == 0) return;

        if (hasChanged) {
            updateQueryParams();

            clearComponentData()

            getAssetUsageCharts({
                dpIds,
                accounts: effectiveServiceLines === "''" && newSummaryServiceAccount?.length === selectedServiceAccount?.length ? [] : selectedServiceAccount,
                startDate: tableParams.startDate.format(YYYYMMDD_HHmmss),
                endDate: tableParams.endDate.format(YYYYMMDD_HHmmss),
                source: "direct_source",
                serviceLines: effectiveServiceLines === "''" ? [] : effectiveServiceLines,
                includeInactiveServiceLines,
            });

            let _parameters = {
                dpIds,
                accounts: effectiveServiceLines === "''" && newSummaryServiceAccount?.length === selectedServiceAccount?.length ? [] : selectedServiceAccount,
                serviceLines: effectiveServiceLines === "''" ? [] : effectiveServiceLines,
                searchText: tableParams.search,
                chartStartDate: tableParams.startDate.format(YYYYMMDD_HHmmss),
                chartEndDate: tableParams.endDate.format(YYYYMMDD_HHmmss),
                sortBy: tableParams.sortBy,
                sortOrder: tableParams.sortOrder,
                page: tableParams.page,
                limit: tableParams.size,
                usageSource: getSource(),
                non_billable: selectedFilters && selectedFilters === "standard" || selectedFilters === "total" ? 1 : 0,
                standard: selectedFilters && selectedFilters === "standard" || selectedFilters === "total" ? 1 : 0,
                priority: selectedFilters && selectedFilters === "total" ? 1 : 0,
                opt_in_priority: selectedFilters && selectedFilters === "standard" || selectedFilters === "total" ? 1 : 0,
            }
            getAssets(_parameters);

            prevTableParamsRef.current = tableParams;
            prevIncludeInactiveServiceLinesRef.current = includeInactiveServiceLines;
        }
    }, [JSON.stringify(tableParams), effectiveServiceLines, JSON.stringify(selectedServiceAccount), selectedFilters, includeInactiveServiceLines,]);

    return (
        <Grid>
            <AssetUsageBarChart selectedAssets={selectedAssets} startDate={tableParams.startDate} defaultFilters={defaultFilters} selectedFilters={selectedFilters} handleFilterChange={handleFilterChange} endDate={tableParams.endDate} includeInactiveServiceLines={includeInactiveServiceLines} onIncludeInactiveServiceLinesChange={setIncludeInactiveServiceLines} />

            <Paper elevation={1} classes={{ root: 'starlink--base--mb_1x starlink--base--padding_05x' }}>
                {/* <div className="download-summary-btn marg-left-auto" onClick={handleDownloadReport}><img className="downloadImg" src={DownloadIcon} alt="" /><Button>Download</Button></div> */}

                <Grid display={"flex"} mb={1}>

                    <Grid container display={'flex'} alignItems={'center'} justifyContent={'flex-end'}>

                        <Grid ml={1}>
                            <TextField
                                id="search"
                                variant="outlined"
                                placeholder="Type something"
                                autoComplete="off"
                                classes={{ root: "input-box-starlink-rp" }}
                                size="small"
                                value={searchText}
                                onChange={handleOnSearchChange}
                                onKeyDown={(e) => { if (e.key === 'Enter') { handleOnSearchClick() } }}
                                InputLabelProps={{ className: "serachLabel" }}
                                InputProps={{
                                    className: "serachBar",
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            {!searchText && <SearchIcon
                                                className="cursorPointer input-search-icons" onClick={handleOnSearchClick}
                                            />}
                                            {searchText && <CloseIcon
                                                className="cursorPointer input-search-icons"
                                                onClick={handleOnSearchClear}
                                            />}
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid ml={1}>
                            <Tooltip classes={{ tooltip: 'starlink--tooltip' }} title={
                                <Typography component={'div'} className="starlink--base--font_08x starlink--base--padding_05x">
                                    The data is retrieved directly from the Starlink cloud and may be subject to delays in availability.
                                </Typography>
                            }>
                                <QuestionMarkOutlinedIcon sx={{ padding: "15px" }} className="marg-left-auto" />
                            </Tooltip>
                        </Grid>
                        <Grid ml={1}>
                            <Button variant="contained" size="small" className='starlink--button_contained--primary' onClick={handleOpenAvailabilityAlertDialog} disabled={false} startIcon={<NotificationAddIcon />}>Availability</Button>
                        </Grid>
                        <Grid ml={1}>
                            {<Button variant="contained" size="small" className='starlink--button_contained--primary' onClick={handleExportCSV} disabled={disableExport || totalAssets === 0} startIcon={<Download />}>Download</Button>}
                        </Grid>
                        <Grid ml={1}>
                            {checkLoggedInUserAuthorizedToViewPage(starlinkAdminService, starlinkAdminFunction) ? <Button variant="contained" size="small" className='starlink--button_contained--primary' onClick={() => { setOpenBillingDialog(true) }} disabled={disableExport || totalAssets === 0} startIcon={<Download />}>Billing</Button> : null}
                        </Grid>
                    </Grid>
                </Grid>


                <Pagination count={totalAssets} onPageChange={handleChangePage} page={tableParams.page} rowsPerPage={tableParams.size} onRowsPerPageChange={handleChangeSize} />
                <AssetsTable startDate={tableParams.startDate} endDate={tableParams.endDate} sortBy={tableParams.sortBy} sortOrder={tableParams.sortOrder} selectedAssets={selectedAssets} onChange={handleChangeSelectedAssets} onChangeSorting={handleChangeSorting} onEditServiceLineQuota={handleEditServiceLineQuota} onEditServiceLineAvailability={handleEditServiceLineAvailability} />
            </Paper>

            <QuotaAlertDialog open={openQuotaAlertDialog || serviceLineQuotaToEdit !== null} onClose={handleCloseQuotaAlertDialog} serviceLineToEdit={serviceLineQuotaToEdit} onSave={handleSetQuotaAlert} />

            <AvailabiltyAlertDialog open={openAvailabilityAlertDialog || serviceLineAvailabilityToEdit !== null} onClose={handleCloseAvailabilityAlertDialog} serviceLineToEdit={serviceLineAvailabilityToEdit} onSave={handleSetAvailabilityAlert} />

            <Dialog
                open={openBillingDialog}
                onClose={() => { setOpenBillingDialog(false); setSelectedDownloadPeriod(currentMonth) }}
                aria-labelledby="service_account--dialog"
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    style: {
                        padding: '2em',
                        borderRadius: '10px',
                        minWidth: '400px'
                    }
                }}
            > <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Typography
                            variant="h5"
                            component="div"
                            align="center"
                            style={{ fontWeight: 'bold', color: "#424141" }}
                        > Download Billing for Month  </Typography>
                    </Grid>

                    <Grid item xs={6}>
                        <Typography fontSize={"0.8rem"}>Select Month</Typography>
                        <Select
                            fullWidth
                            size="small"
                            value={selectedDownloadPeriod?.value || ''}
                            onChange={(e) => {
                                const selectedMonth = lastThreeMonthsWithCurrent.find(month => month.value === e.target.value);
                                setSelectedDownloadPeriod(selectedMonth);
                            }}
                            displayEmpty
                        >
                            <MenuItem value="" disabled>Select Duration</MenuItem>
                            {lastThreeMonthsWithCurrent.map((month) => (
                                <MenuItem key={month.value} value={month.value}>
                                    {month.label} </MenuItem>
                            ))}
                        </Select>
                    </Grid>

                    <Grid item xs={12} display="flex" justifyContent="flex-end">
                        <Button
                            variant="outlined" className='starlink--button--cancel'
                            style={{ marginRight: '10px' }}
                            onClick={() => { setOpenBillingDialog(false); setSelectedDownloadPeriod(currentMonth) }}
                        > Cancel
                        </Button>
                        <Button
                            variant="outlined" className='starlink--button_contained--primary'
                            onClick={() => { handleDownloadBillingReport() }}
                        >Download </Button>
                    </Grid>
                </Grid>
            </Dialog>


        </Grid>
    );
}

const mapStateToProps = (state) => ({
    authReducer: state.authReducer,
    errorReducer: state.errorReducer,
    dpIds: state.starlinkCloud.dpIds,
    commonFilters: state.starlinkCloud.commonFilters,
    assets: state.starlinkReports.assets,
    assetsCSV: state.starlinkReports.assetsCSV,
    assetUsage: state.starlinkReports.assetUsage,
    totalAssets: state.starlinkReports.totalAssets,
    newSummaryStartDate: state?.authReducer?.newSummaryStartDate,
    newSummaryEndDate: state?.authReducer?.newSummaryEndDate,
    newSummaryServiceLine: state?.authReducer?.newSummaryServiceLine,
    serviceAccounts: state.starlinkAdmin.serviceAccounts,
    selectedAccount: state.starlinkCloud.selectedAccount,
    accountsData: state.starlinkCloud.accountsData,
    assetUsageSplit: state.starlinkReports.assetUsageSplit,
    selectedServiceAccount: state?.authReducer?.selectedServiceAccount,
    hasUserChangedServiceLine: state?.authReducer?.hasUserChangedServiceLine,
    newSummaryServiceAccount: state?.authReducer?.newSummaryServiceAccount,
    getSubscribedServiceLines: state?.starlinkDashboard?.getSubscribedServiceLines
});

export default withRouter(
    connect(mapStateToProps, {
        updatePermission,
        parentDpAndSubDpList,
        getAssets,
        getAssetUsage,
        getAssetsCSV,
        setQuotaAlert,
        setAvailabilityAlert,
        clearComponentData,
        clearCSV,
        downloadAsCSV,
        getAllServiceLines,
        setSelectedAccount,
        getAssetUsageCharts
    })(StarlinkReports)
);